import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { Logo } from "@/components/Logo";

const sections = [
  {
    heading: "Privacy First",
    body: [
      "At Affra, protecting your data is not just a legal obligation — it is a core principle of how we operate.",
      "We believe that the businesses using our platform should own and control their data, not surrender it to the technology that supports them.",
      "Unlike many technology platforms, Affra is designed to provide infrastructure, not ownership. Our role is to securely process information on behalf of our clients while ensuring they retain full control over their data.",
    ],
  },
  {
    heading: "Our Role",
    body: [
      "For ticketing and event management services provided through our platform, Affra acts as a data processor on behalf of the event organiser, who is the data controller.",
      "This means:",
    ],
    list: [
      "The event organiser decides what personal data is collected and why it is processed.",
      "Affra processes that data solely in accordance with the event organiser's instructions.",
      "We do not use customer data for our own commercial purposes.",
    ],
    after: "For information relating to your Affra account, billing, support requests, and operation of our own website, Affra acts as the data controller and processes that information in accordance with applicable data protection laws.",
  },
  {
    heading: "Data Ownership",
    body: [
      "Your data belongs to you.",
      "We do not claim ownership of any customer information, event data, ticketing records, sales information, or business intelligence stored within our platform.",
      "Our clients retain ownership of all data processed through Affra.",
    ],
  },
  {
    heading: "We Never Sell Your Data",
    body: ["Affra will never:"],
    list: [
      "Sell your personal data.",
      "Sell your customer lists.",
      "Sell behavioural or purchasing information.",
      "License your data to third parties.",
      "Monetise your information through advertising.",
      "Build advertising profiles using your customers.",
    ],
    after: "Your information is never treated as a product.",
  },
  {
    heading: "No Unauthorised Access",
    body: ["We do not access your data unless it is:"],
    list: [
      "Required to provide technical support you have requested;",
      "Necessary to maintain or secure the platform;",
      "Required to comply with a legal obligation; or",
      "Specifically authorised by you.",
    ],
    after: "Where access is required for support purposes, it is limited to the minimum information necessary to resolve the issue.",
  },
  {
    heading: "No Unauthorised Sharing",
    body: ["Your information will never be:"],
    list: [
      "Shared with third parties for marketing purposes;",
      "Transferred to another organisation for commercial gain;",
      "Used to compete with your business;",
      "Accessed by other Affra customers.",
    ],
    after: "Any sharing of information occurs only where necessary to deliver the service, comply with legal obligations, or where you have provided explicit permission.",
  },
  {
    heading: "Payment Processing",
    body: [
      "Affra facilitates secure payment processing on behalf of event organisers and venues through trusted, PCI DSS-compliant third-party payment service providers.",
      "Affra does not store full payment card details, including complete card numbers, CVV/CVC security codes, or PINs. All payment information is transmitted securely and processed directly by the appointed payment processor in accordance with industry security standards.",
      "Information associated with a payment transaction, such as transaction identifiers, payment status, payment method, timestamps and order references, may be processed by Affra where necessary to fulfil ticket purchases, issue refunds, provide customer support, prevent fraud, reconcile transactions and maintain accurate financial records.",
    ],
  },
  {
    heading: "Customer Information Collected",
    body: [
      "The information collected from consumers when purchasing tickets is determined by the event organiser or venue, who acts as the Data Controller.",
      "Affra provides the tools to collect and process this information but does so solely on the instructions of the event organiser.",
      "Depending on the configuration chosen by the event organiser, information collected may include:",
    ],
    list: [
      "Full name",
      "Email address",
      "Telephone number",
      "Postal address",
      "Date of birth",
      "Emergency contact details",
      "Accessibility or special assistance requirements",
      "Marketing preferences and communication consent",
      "Ticket holder details",
      "Membership or loyalty information",
      "Vehicle registration details (where required)",
      "Any additional information specifically requested by the event organiser to facilitate attendance or comply with legal or operational requirements.",
    ],
    after: "The event organiser is responsible for determining what information is collected; whether particular fields are mandatory or optional; the lawful basis for processing that information; and how long the information is retained. Affra does not determine what customer information is collected beyond what is necessary to operate the platform and fulfil the services requested by the event organiser.",
  },
  {
    heading: "Our Commitment",
    body: [
      "We collect and process only the information necessary to deliver secure, reliable ticketing services.",
      "We do not use consumer information for advertising, profiling, or commercial resale, and we do not collect additional personal information beyond that required by the event organiser or necessary for the operation, security and integrity of the Affra platform.",
    ],
  },
  {
    heading: "Security",
    body: ["We implement appropriate technical and organisational measures designed to protect your information against:"],
    list: [
      "Unauthorised access;",
      "Loss or destruction;",
      "Alteration;",
      "Disclosure;",
      "Misuse.",
    ],
    after: "Security is built into every aspect of the Affra platform and is continually reviewed to meet modern industry standards.",
  },
  {
    heading: "Transparency",
    body: [
      "We believe trust is earned through transparency.",
      "If we process your information, we will do so lawfully, fairly, and only for the purposes for which it was collected.",
      "We do not believe in hidden data practices or undisclosed commercial use of customer information.",
    ],
  },
  {
    heading: "Your Rights",
    body: ["Depending on your relationship with Affra and applicable law, you may have rights including:"],
    list: [
      "Access to your personal information;",
      "Correction of inaccurate information;",
      "Deletion of personal information where appropriate;",
      "Restriction of processing;",
      "Data portability;",
      "Objection to certain types of processing.",
    ],
    after: "Where Affra is acting as a data processor, requests relating to personal data should generally be directed to the relevant event organiser, who is responsible for determining how your data is processed.",
  },
  {
    heading: "International Transfers",
    body: [
      "Where personal information is transferred internationally, Affra will ensure appropriate safeguards are in place in accordance with applicable data protection legislation.",
    ],
  },
  {
    heading: "Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time to reflect changes in legislation, technology, or our services. The latest version will always be published on our website together with its effective date.",
    ],
  },
  {
    heading: "Business Continuity, Sale or Acquisition",
    body: [
      "Affra has been built on the principle that our clients own their data — not us.",
      "Accordingly, customer data is not considered a commercial asset of Affra and will never be bought, sold, licensed or otherwise traded as part of any business transaction.",
      "Should Affra ever undergo a sale, merger, acquisition, investment, restructuring or change of ownership, our intention is that any transaction will be structured, wherever reasonably practicable, as a business asset purchase relating to our intellectual property, technology, software, brand and operational assets, excluding customer data.",
      "Your customer information, sales records, ticketing data and business intelligence will remain under your control and will continue to be processed only in accordance with your instructions and applicable data protection law.",
      "If any proposed transaction would require a different approach under applicable law, we will:",
    ],
    list: [
      "Notify affected clients in advance wherever legally permitted;",
      "Continue to honour our contractual and data protection obligations;",
      "Never sell or transfer customer data as a commercial asset for financial gain; and",
      "Ensure any successor is bound by the same privacy, confidentiality and security obligations that apply to Affra.",
    ],
    after: "Our commitment is simple: your data is yours. It is never part of the value of our business, and it will never be treated as a product that can be sold to another organisation.",
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-32 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid={NAV.logo}>
            <Logo size="h-28" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ legal</div>
        <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">
          Privacy Policy
        </h1>
        <div className="mt-4 text-sm text-zinc-500">Effective Date: 10 July 2026</div>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <section key={s.heading} className="scroll-mt-24" id={`section-${i + 1}`}>
              <h2 className="font-display font-bold text-2xl tracking-tight flex gap-3 items-baseline">
                <span className="text-zinc-400 font-mono text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span>{s.heading}</span>
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-zinc-700 pl-9">
                {s.body.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
                {s.list && (
                  <ul className="list-disc pl-5 space-y-1.5 marker:text-zinc-400">
                    {s.list.map((li, idx) => (
                      <li key={idx}>{li}</li>
                    ))}
                  </ul>
                )}
                {s.after && <p>{s.after}</p>}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-200 text-sm text-zinc-500">
          Questions about this policy? Reach out to <a href="mailto:privacy@affra.io" className="text-indigo-600 hover:underline">privacy@affra.io</a>. See also our <Link to="/terms" className="text-indigo-600 hover:underline">Terms of Service</Link>.
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-sm text-zinc-500 flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Affra — white-label ticketing.</div>
          <Link to="/" className="hover:text-zinc-900">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
